import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    FormGroup,
    FormLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import React, { useEffect, useRef, useState } from "react";

// Quartz cron format: seconds minutes hours day-of-month month day-of-week [year]
const DAYS_OF_WEEK = [
    { value: "SUN", label: "Sunday" },
    { value: "MON", label: "Monday" },
    { value: "TUE", label: "Tuesday" },
    { value: "WED", label: "Wednesday" },
    { value: "THU", label: "Thursday" },
    { value: "FRI", label: "Friday" },
    { value: "SAT", label: "Saturday" }
];

const MONTHS = [
    { value: "1", label: "January" },
    { value: "2", label: "February" },
    { value: "3", label: "March" },
    { value: "4", label: "April" },
    { value: "5", label: "May" },
    { value: "6", label: "June" },
    { value: "7", label: "July" },
    { value: "8", label: "August" },
    { value: "9", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" }
];

const SCHEDULE_TYPES = [
    { value: "every_minute", label: "Every Minute" },
    { value: "every_hour", label: "Every Hour" },
    { value: "every_day", label: "Every Day" },
    { value: "every_week", label: "Every Week" },
    { value: "every_month", label: "Every Month" },
    { value: "custom", label: "Custom" }
];

export const CRON_PRESETS = [
    { value: "1", label: "1", cron: "0 0/5 * * * ?", description: "Every 5 minutes" },
    { value: "2", label: "2", cron: "0 0 9 ? * MON,FRI", description: "Mon & Fri 9 AM" },
    { value: "3", label: "3", cron: "0 30 8 ? * MON-FRI", description: "Weekdays 8:30 AM" },
    {
        value: "4",
        label: "4",
        cron: "27 52 6,11,15,20 ? * 1,3",
        description: "Mon & Wed at 6:52, 11:52, 3:52 PM, 8:52 PM",
    },
    {
        value: "5",
        label: "5",
        cron: "19 33 7,12,16,22 ? * 2,4",
        description: "Tue & Thu at 7:33, 12:33, 4:33 PM, 10:33 PM",
    },
    {
        value: "6",
        label: "6",
        cron: "22 41 13,18 ? * 5-7",
        description: "Fri–Sun at 1:41 PM, 6:41 PM",
    },
];

export const SOCIAL_POST_CRON_PRESETS = [
    {
        value: "7",
        label: "7",
        cron: "42 15 6,11,15,20 ? * 1,3",
        description: "Mon & Wed at 6:15, 11:15, 3:15 PM, 8:15 PM",
    },
    {
        value: "8",
        label: "8",
        cron: "11 48 7,12,16,22 ? * 2,4",
        description: "Tue & Thu at 7:48, 12:48, 4:48 PM, 10:48 PM",
    },
    {
        value: "9",
        label: "9",
        cron: "0 34 13,18 ? * 5-7",
        description: "Fri–Sun at 1:34 PM, 6:34 PM",
    },
];

/** Full preset list (1–9) for admin schedule forms (task scheduler, publisher, etc.) */
export const ADMIN_CRON_PRESETS = [...CRON_PRESETS, ...SOCIAL_POST_CRON_PRESETS];

const isValidCronField = (field, min, max) => {
    if (field === "*") return true;
    if (field.includes("/")) {
        const [range, step] = field.split("/");
        if (range !== "*" && !isValidCronField(range, min, max)) return false;
        const stepNum = parseInt(step, 10);
        return !isNaN(stepNum) && stepNum >= 1;
    }
    if (field.includes("-")) {
        const [start, end] = field.split("-");
        const startNum = parseInt(start, 10);
        const endNum = parseInt(end, 10);
        return !isNaN(startNum) && !isNaN(endNum) && startNum >= min && endNum <= max && startNum <= endNum;
    }
    if (field.includes(",")) {
        return field.split(",").every(part => isValidCronField(part.trim(), min, max));
    }
    const num = parseInt(field, 10);
    return !isNaN(num) && num >= min && num <= max;
};

const isValidDayOfMonth = (field) => {
    if (field === "*" || field === "?" || field === "L" || field === "LW") return true;
    if (field.match(/^L-\d+$/)) return true;
    if (field.match(/^\d+W$/)) return true;
    return isValidCronField(field, 1, 31);
};

const isValidMonth = (field) => {
    if (field === "*") return true;
    const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
    if (field.includes(",")) {
        return field.split(",").every(part => isValidMonth(part.trim()));
    }
    if (field.includes("-")) {
        const [start, end] = field.split("-");
        return (isValidMonth(start) && isValidMonth(end));
    }
    if (monthNames.includes(field.toUpperCase())) return true;
    return isValidCronField(field, 1, 12);
};

const isValidDayOfWeek = (field) => {
    if (field === "*" || field === "?" || field === "L") return true;
    const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    if (field.includes("#")) {
        const [day, nth] = field.split("#");
        const nthNum = parseInt(nth, 10);
        if (isNaN(nthNum) || nthNum < 1 || nthNum > 5) return false;
        return isValidDayOfWeek(day);
    }
    if (field.endsWith("L") && field.length > 1) {
        return isValidDayOfWeek(field.slice(0, -1));
    }
    if (field.includes(",")) {
        return field.split(",").every(part => isValidDayOfWeek(part.trim()));
    }
    if (field.includes("-")) {
        const [start, end] = field.split("-");
        return (isValidDayOfWeek(start) && isValidDayOfWeek(end));
    }
    if (dayNames.includes(field.toUpperCase())) return true;
    return isValidCronField(field, 1, 7);
};

export function validateCronExpression(cron) {
    if (!cron || cron.trim() === "") {
        return { valid: false, error: "Cron expression is required" };
    }
    const parts = cron.trim().split(/\s+/);
    if (parts.length < 6 || parts.length > 7) {
        return { valid: false, error: "Must have 6 or 7 fields (seconds minutes hours day-of-month month day-of-week [year])" };
    }
    const [seconds, minutes, hours, dayOfMonth, month, dayOfWeek] = parts;
    if (!isValidCronField(seconds, 0, 59)) {
        return { valid: false, error: "Invalid seconds field (0-59)" };
    }
    if (!isValidCronField(minutes, 0, 59)) {
        return { valid: false, error: "Invalid minutes field (0-59)" };
    }
    if (!isValidCronField(hours, 0, 23)) {
        return { valid: false, error: "Invalid hours field (0-23)" };
    }
    if (!isValidDayOfMonth(dayOfMonth)) {
        return { valid: false, error: "Invalid day-of-month field (1-31, *, ?, L, W)" };
    }
    if (!isValidMonth(month)) {
        return { valid: false, error: "Invalid month field (1-12 or JAN-DEC)" };
    }
    if (!isValidDayOfWeek(dayOfWeek)) {
        return { valid: false, error: "Invalid day-of-week field (1-7, SUN-SAT, *, ?, L, #)" };
    }
    if (dayOfMonth !== "?" && dayOfWeek !== "?" && dayOfMonth !== "*" && dayOfWeek !== "*") {
        return { valid: false, error: "Day-of-month and day-of-week cannot both be specified (use ? for one)" };
    }
    return { valid: true, error: null };
}

/** Pure parse: returns initial state from cron string to avoid flicker on first paint */
function parseCronToInitialState(cronStr) {
    const defaultState = {
        scheduleType: "every_day",
        cronParts: {
            seconds: "0",
            minutes: "0",
            hours: "0",
            dayOfMonth: "*",
            month: "*",
            dayOfWeek: "?"
        },
        selectedDays: [],
        selectedMonths: []
    };
    if (!cronStr || typeof cronStr !== "string") return defaultState;
    const parts = cronStr.trim().split(" ");
    if (parts.length < 6) return defaultState;
    const cronParts = {
        seconds: parts[0] || "0",
        minutes: parts[1] || "0",
        hours: parts[2] || "0",
        dayOfMonth: parts[3] || "*",
        month: parts[4] || "*",
        dayOfWeek: parts[5] || "?"
    };
    const selectedDays = parts[5] && parts[5] !== "?" && parts[5] !== "*" ? parts[5].split(",") : [];
    const selectedMonths = parts[4] && parts[4] !== "*" ? parts[4].split(",") : [];
    const hasListOrStep = (p) => p.includes(",") || p.includes("/");
    let scheduleType = "custom";
    if (hasListOrStep(parts[2]) || hasListOrStep(parts[1]) || hasListOrStep(parts[0])) {
        scheduleType = "custom";
    } else if (parts[0] === "0" && parts[1] === "*" && parts[2] === "*") scheduleType = "every_minute";
    else if (parts[0] === "0" && parts[1] === "0" && parts[2] === "*") scheduleType = "every_hour";
    else if (parts[3] === "*" && parts[5] === "?") scheduleType = "every_day";
    else if (parts[5] !== "?" && parts[5] !== "*") scheduleType = "every_week";
    else if (parts[3] !== "*" || parts[4] !== "*") scheduleType = "every_month";
    return { scheduleType, cronParts, selectedDays, selectedMonths };
}

export default function CronExpressionBuilder({ value, onChange, presets = CRON_PRESETS }) {
    const initial = React.useMemo(() => parseCronToInitialState(value), []);
    const [scheduleType, setScheduleType] = useState(initial.scheduleType);
    const [cronParts, setCronParts] = useState(initial.cronParts);
    const [selectedDays, setSelectedDays] = useState(initial.selectedDays);
    const [selectedMonths, setSelectedMonths] = useState(initial.selectedMonths);
    const [draftCron, setDraftCron] = useState(() => value || "");
    const [cronError, setCronError] = useState(null);
    const [selectedPreset, setSelectedPreset] = useState(null);
    const [openPresetDialog, setOpenPresetDialog] = useState(false);
    const isFirstBuildEffectRun = useRef(true);
    /** Parent `value` we last applied — avoids re-parsing our own onChange updates. */
    const lastExternalValueRef = useRef(value);

    const parseCronExpression = (cronStr, { updateScheduleType = true } = {}) => {
        if (!cronStr) return;
        const parts = cronStr.trim().split(/\s+/);
        if (parts.length < 6) return;
        const newParts = {
            seconds: parts[0] || "0",
            minutes: parts[1] || "0",
            hours: parts[2] || "0",
            dayOfMonth: parts[3] || "*",
            month: parts[4] || "*",
            dayOfWeek: parts[5] || "?"
        };
        setCronParts(newParts);
        if (parts[5] && parts[5] !== "?" && parts[5] !== "*") {
            setSelectedDays(parts[5].split(","));
        } else {
            setSelectedDays([]);
        }
        if (parts[4] && parts[4] !== "*") {
            setSelectedMonths(parts[4].split(","));
        } else {
            setSelectedMonths([]);
        }
        if (!updateScheduleType) return;
        const hasListOrStep = (p) => p.includes(",") || p.includes("/");
        if (hasListOrStep(parts[2]) || hasListOrStep(parts[1]) || hasListOrStep(parts[0])) {
            setScheduleType("custom");
        } else if (parts[0] === "0" && parts[1] === "*" && parts[2] === "*") {
            setScheduleType("every_minute");
        } else if (parts[0] === "0" && parts[1] === "0" && parts[2] === "*") {
            setScheduleType("every_hour");
        } else if (parts[3] === "*" && parts[5] === "?") {
            setScheduleType("every_day");
        } else if (parts[5] !== "?" && parts[5] !== "*") {
            setScheduleType("every_week");
        } else if (parts[3] !== "*" || parts[4] !== "*") {
            setScheduleType("every_month");
        } else {
            setScheduleType("custom");
        }
    };

    const buildCronExpression = () => {
        const { seconds, minutes, hours, dayOfMonth, month, dayOfWeek } = cronParts;
        return `${seconds} ${minutes} ${hours} ${dayOfMonth} ${month} ${dayOfWeek}`;
    };

    /** Sync when parent value changes externally (e.g. load on edit), not from our own onChange. */
    useEffect(() => {
        if (value === lastExternalValueRef.current) return;
        lastExternalValueRef.current = value;
        setDraftCron(value || "");
        if (value) {
            parseCronExpression(value);
        }
    }, [value]);

    useEffect(() => {
        if (scheduleType === "custom") return;
        const built = buildCronExpression();
        if (!isFirstBuildEffectRun.current && built !== value) {
            lastExternalValueRef.current = built;
            setDraftCron(built);
            onChange(built);
        }
        isFirstBuildEffectRun.current = false;
        const validation = validateCronExpression(built);
        setCronError(validation.valid ? null : validation.error);
    }, [cronParts, scheduleType]);

    const handleScheduleTypeChange = (event) => {
        const type = event.target.value;
        setScheduleType(type);
        let newParts = { ...cronParts };
        switch (type) {
            case "every_minute":
                newParts = { seconds: "0", minutes: "*", hours: "*", dayOfMonth: "*", month: "*", dayOfWeek: "?" };
                setSelectedDays([]);
                setSelectedMonths([]);
                break;
            case "every_hour":
                newParts = { seconds: "0", minutes: "0", hours: "*", dayOfMonth: "*", month: "*", dayOfWeek: "?" };
                setSelectedDays([]);
                setSelectedMonths([]);
                break;
            case "every_day":
                newParts = { ...newParts, dayOfMonth: "*", month: "*", dayOfWeek: "?" };
                setSelectedDays([]);
                setSelectedMonths([]);
                break;
            case "every_week":
                newParts = { ...newParts, dayOfMonth: "?", month: "*", dayOfWeek: selectedDays.length > 0 ? selectedDays.join(",") : "MON" };
                if (selectedDays.length === 0) setSelectedDays(["MON"]);
                setSelectedMonths([]);
                break;
            case "every_month":
                newParts = { ...newParts, dayOfMonth: "1", dayOfWeek: "?" };
                setSelectedDays([]);
                break;
            default:
                break;
        }
        setCronParts(newParts);
    };

    const handleCronPartChange = (field) => (event) => {
        setCronParts((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleDayOfWeekChange = (day) => {
        setSelectedDays((prev) => {
            const newDays = prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day];
            setCronParts((prevParts) => ({
                ...prevParts,
                dayOfWeek: newDays.length > 0 ? newDays.join(",") : "?"
            }));
            return newDays;
        });
    };

    const handleMonthChange = (month) => {
        setSelectedMonths((prev) => {
            const newMonths = prev.includes(month) ? prev.filter((m) => m !== month) : [...prev, month];
            setCronParts((prevParts) => ({
                ...prevParts,
                month: newMonths.length > 0 ? newMonths.join(",") : "*"
            }));
            return newMonths;
        });
    };

    const handleCronManualChange = (event) => {
        const newValue = event.target.value;
        setScheduleType("custom");
        setDraftCron(newValue);
        lastExternalValueRef.current = newValue;
        onChange(newValue);
        setSelectedPreset(null);
        const validation = validateCronExpression(newValue);
        setCronError(validation.valid ? null : validation.error);
        if (newValue.trim().split(/\s+/).length >= 6) {
            parseCronExpression(newValue, { updateScheduleType: false });
        }
    };

    const handlePresetChange = (event, newPreset) => {
        setSelectedPreset(newPreset);
        if (newPreset) {
            const preset = presets.find(p => p.value === newPreset);
            if (preset) {
                lastExternalValueRef.current = preset.cron;
                setDraftCron(preset.cron);
                onChange(preset.cron);
                setScheduleType("custom");
                parseCronExpression(preset.cron);
                setCronError(null);
            }
        }
    };

    const generateHourOptions = () => {
        const options = [];
        for (let i = 0; i < 24; i++) {
            options.push(<MenuItem key={i} value={String(i)}>{i.toString().padStart(2, "0")}:00</MenuItem>);
        }
        return options;
    };

    const generateMinuteOptions = () => {
        const options = [];
        for (let i = 0; i < 60; i += 5) {
            options.push(<MenuItem key={i} value={String(i)}>:{i.toString().padStart(2, "0")}</MenuItem>);
        }
        return options;
    };

    const generateDayOfMonthOptions = () => {
        const options = [];
        for (let i = 1; i <= 31; i++) {
            options.push(<MenuItem key={i} value={String(i)}>{i}</MenuItem>);
        }
        return options;
    };

    return (
        <>
            <Grid container sx={{ mt: 1, mb: 2, p: { xs: 1.5, sm: 2 }, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden" }} spacing={2} disableEqualOverflow>
                <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Schedule Configuration
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl sx={{ minWidth: { xs: "100%", sm: 200 } }} margin="dense" fullWidth>
                        <InputLabel>Schedule Type</InputLabel>
                        <Select
                            value={scheduleType}
                            onChange={handleScheduleTypeChange}
                            label="Schedule Type"
                        >
                            {SCHEDULE_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {scheduleType !== "every_minute" && scheduleType !== "custom" && (
                    <>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <FormControl sx={{ minWidth: { xs: "100%", sm: 100 } }} margin="dense" fullWidth>
                                <InputLabel>Hour</InputLabel>
                                <Select
                                    value={cronParts.hours === "*" ? "0" : cronParts.hours}
                                    onChange={handleCronPartChange("hours")}
                                    label="Hour"
                                    disabled={scheduleType === "every_hour"}
                                >
                                    {generateHourOptions()}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                            <FormControl sx={{ minWidth: { xs: "100%", sm: 100 } }} margin="dense" fullWidth>
                                <InputLabel>Minute</InputLabel>
                                <Select
                                    value={cronParts.minutes === "*" ? "0" : cronParts.minutes}
                                    onChange={handleCronPartChange("minutes")}
                                    label="Minute"
                                >
                                    {generateMinuteOptions()}
                                </Select>
                            </FormControl>
                        </Grid>
                    </>
                )}

                {scheduleType === "every_week" && (
                    <Grid size={12} sx={{ mt: 2 }}>
                        <FormControl component="fieldset" sx={{ width: "100%" }}>
                            <FormLabel component="legend">Days of Week</FormLabel>
                            <FormGroup row sx={{ flexWrap: "wrap" }}>
                                {DAYS_OF_WEEK.map((day) => (
                                    <FormControlLabel
                                        key={day.value}
                                        control={
                                            <Checkbox
                                                checked={selectedDays.includes(day.value)}
                                                onChange={() => handleDayOfWeekChange(day.value)}
                                            />
                                        }
                                        label={day.label.substring(0, 3)}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                    </Grid>
                )}

                {scheduleType === "every_month" && (
                    <Grid size={{ xs: 12, sm: 4 }} sx={{ mt: 2 }}>
                        <FormControl sx={{ minWidth: { xs: "100%", sm: 150 } }} fullWidth>
                            <InputLabel>Day of Month</InputLabel>
                            <Select
                                value={cronParts.dayOfMonth === "*" || cronParts.dayOfMonth === "?" ? "1" : cronParts.dayOfMonth}
                                onChange={handleCronPartChange("dayOfMonth")}
                                label="Day of Month"
                            >
                                {generateDayOfMonthOptions()}
                            </Select>
                        </FormControl>
                    </Grid>
                )}

                {scheduleType === "every_month" && (
                    <Grid size={12} sx={{ mt: 2 }}>
                        <FormControl component="fieldset" sx={{ width: "100%" }}>
                            <FormLabel component="legend">Months (leave empty for all months)</FormLabel>
                            <FormGroup row sx={{ flexWrap: "wrap" }}>
                                {MONTHS.map((month) => (
                                    <FormControlLabel
                                        key={month.value}
                                        control={
                                            <Checkbox
                                                checked={selectedMonths.includes(month.value)}
                                                onChange={() => handleMonthChange(month.value)}
                                            />
                                        }
                                        label={month.label.substring(0, 3)}
                                    />
                                ))}
                            </FormGroup>
                        </FormControl>
                    </Grid>
                )}

                <Grid size={12} sx={{ mt: 2 }}>
                    <TextField
                        margin="dense"
                        label="Cron Expression (Quartz format)"
                        variant="outlined"
                        value={draftCron}
                        onChange={handleCronManualChange}
                        error={!!cronError}
                        helperText={cronError
                            ? cronError
                            : scheduleType !== "custom"
                                ? "Edit directly to switch to custom mode"
                                : "Format: sec min hr day-of-month month day-of-week"
                        }
                        fullWidth
                        sx={{ "& .MuiInputBase-input": { minWidth: { sm: 260 } } }}
                    />
                </Grid>
                <Grid size={12} sx={{ mt: 1, overflow: "hidden", minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", maxWidth: "100%" }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Preset
                        </Typography>
                        <ToggleButtonGroup
                            value={selectedPreset}
                            exclusive
                            onChange={handlePresetChange}
                            size="small"
                            sx={{ flexWrap: "wrap" }}
                        >
                            {presets.map((preset) => (
                                <ToggleButton
                                    key={preset.value}
                                    value={preset.value}
                                    title={`${preset.description}: ${preset.cron}`}
                                    sx={{ minWidth: 36 }}
                                >
                                    {preset.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    </Box>
                    {selectedPreset && (() => {
                        const preset = presets.find((p) => p.value === selectedPreset);
                        return preset ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                {preset.description}
                            </Typography>
                        ) : null;
                    })()}
                </Grid>
            </Grid>

            <Dialog open={openPresetDialog} onClose={() => setOpenPresetDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Preset Descriptions</DialogTitle>
                <DialogContent>
                    <Grid container sx={{ mt: 1 }} spacing={1} disableEqualOverflow>
                        {presets.map((preset) => (
                            <Grid size={12} key={preset.value}>
                                <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                                    <strong>{preset.label}</strong>: <code>{preset.cron}</code> - {preset.description}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPresetDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
